/**
 * UcFormX — форма «Обмен UC → ₽» (screen-22, эталон renderFormX js:99-131;
 * в V1 UI обмена не было — форма новая, флоу A26+). Cyan-вариант cform
 * (qcss.cform + ucss.cformCy). Поля: оператор, дата зарплаты (Dropdown
 * ближайших 3 дат грейда 10-е/25-е — УД3), UC (NumPadPop decimals 2 + suffix
 * UC — УД4) и ₽ (NumPadPop money целые — УД8). Guard баланса — клиентский
 * inline ferr, серверный отказ 400 приходит тем же ferr (УД9, не alert).
 * fhint — судьба обмена в зарплате (period_date → finance.ts). Успех — хук
 * перезагрузил данные и вспыхнул первой строкой Обменов, форма закрыта.
 */
import { useMemo, useState } from "react";
import { Dropdown, NumPadPop } from "../../components/shared";
import type { UcBalanceRow, UcExchangeBody } from "../../api/ucTypes";
import { CheckIco, FCell, SelBtn, opItems } from "./UcFormParts";
import { fmtDD, fmtM, fmtUC, NB, zpDates } from "./ucFmt";
import cfg from "./config.module.css";
import qcss from "./quests.module.css";
import ucss from "./uc.module.css";

export function UcFormX({ balances, busy, submit, onClose }: {
  balances: UcBalanceRow[];
  busy: boolean;
  submit: (b: UcExchangeBody) => Promise<string | null>;
  onClose: () => void;
}) {
  const [op, setOp] = useState<string | null>(null);
  const [uc, setUc] = useState(0);
  const [rub, setRub] = useState(0);
  const [date, setDate] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [zd, setZd] = useState<HTMLElement | null>(null);
  const [np, setNp] = useState<{ kind: "uc" | "rub"; anchor: HTMLElement } | null>(null);
  const zp = useMemo(zpDates, []);

  const balRow = op ? balances.find((b) => b.operator_id === op) : undefined;
  const opName = balRow?.operator_name ?? null;
  /* Клиентский guard баланса (УД9, эталон js:118-119). */
  const short = !!balRow && uc > balRow.balance;
  const valid = !!op && uc > 0 && rub > 0 && !!date && !short;

  const doSubmit = () => {
    if (!op || !date || !valid || busy) return;
    setErr(null);
    void submit({ operator_id: op, uc_amount: uc, money_amount: rub, period_date: date })
      .then((e) => (e ? setErr(e) : onClose()));
  };

  return (
    <div className={`${qcss.cform} ${ucss.cformCy}`}>
      <div className={qcss.s17head} style={{ margin: 0, height: 20 }}>
        <h3 className={`${cfg.secT} ${cfg.cy}`} style={{ fontSize: 10 }}>Обмен UC → ₽</h3>
      </div>
      <div className={ucss.frow2}>
        <FCell label="Оператор">
          <SelBtn value={opName} ph="выбрать…" onOpen={(e) => setDd(e.currentTarget)} />
        </FCell>
        <FCell label="Дата зарплаты">
          <SelBtn
            value={date ? <span className={qcss.num}>{fmtDD(date)}</span> : null}
            ph="дата…"
            onOpen={(e) => setZd(e.currentTarget)}
          />
        </FCell>
      </div>
      <div className={ucss.frow2}>
        <FCell label="Юникоины">
          <button
            type="button"
            className={`${cfg.teNumbtn} ${ucss.tnUc}`}
            onClick={(e) => setNp({ kind: "uc", anchor: e.currentTarget })}
          >
            <span className={`${ucss.tv} ${qcss.num}`}>
              {uc > 0 ? `−${fmtUC(uc)}${NB}UC` : <span className={ucss.ph}>0 UC</span>}
            </span>
          </button>
        </FCell>
        <FCell label="Рубли">
          <button
            type="button"
            className={`${cfg.teNumbtn} ${ucss.tnRub}`}
            onClick={(e) => setNp({ kind: "rub", anchor: e.currentTarget })}
          >
            <span className={`${ucss.tv} ${qcss.num}`}>
              {rub > 0 ? `+₽${fmtM(rub)}` : <span className={ucss.ph}>₽0</span>}
            </span>
          </button>
        </FCell>
      </div>
      {short && balRow ? (
        <div className={ucss.ferr}>Недостаточно юникоинов (баланс: {fmtUC(balRow.balance)}{NB}UC)</div>
      ) : err ? (
        <div className={ucss.ferr}>{err}</div>
      ) : null}
      <div className={ucss.fhint}>Упадёт в зарплату этой датой — оператор увидит +₽ в расчёте.</div>
      <div className={qcss.apair} style={{ marginTop: 3 }}>
        <button type="button" className={`${qcss.abtn} ${qcss.cf}`} disabled={!valid || busy} onClick={doSubmit}>
          <CheckIco />Обменять
        </button>
        <button type="button" className={`${qcss.abtn} ${qcss.cl}`} onClick={onClose}>Отмена</button>
      </div>
      <Dropdown
        open={!!dd}
        anchor={dd}
        multi={false}
        filter
        selectedIds={op ? [op] : []}
        items={opItems(balances)}
        onClose={() => setDd(null)}
        onCommit={(ids) => { const id = ids[0]; if (id != null) { setOp(String(id)); setErr(null); } }}
      />
      <Dropdown
        open={!!zd}
        anchor={zd}
        multi={false}
        selectedIds={date ? [date] : []}
        items={zp.map((z) => ({ id: z, label: fmtDD(z), meta: "зарплата" }))}
        onClose={() => setZd(null)}
        onCommit={(ids) => { const id = ids[0]; if (id != null) { setDate(String(id)); setErr(null); } }}
      />
      <NumPadPop
        open={!!np}
        anchor={np?.anchor ?? null}
        label={np?.kind === "rub" ? "В зарплату" : "Списать UC"}
        mode="money"
        suffix={np?.kind === "rub" ? undefined : "UC"}
        decimals={np?.kind === "rub" ? undefined : 2}
        value={np?.kind === "rub" ? rub || 0 : uc || 0}
        onCommit={(v) => {
          if (np?.kind === "rub") setRub(v || 0);
          else setUc(v || 0);
          setErr(null);
        }}
        onClose={() => setNp(null)}
      />
    </div>
  );
}
